// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type {
  AboutYouSectionContent,
  BreakthroughProposalDetails,
  Proposal,
  ProposalSection,
} from '../../../types/proposal';
import { BREAKTHROUGH_ADDABLE_SECTION_KEYS, BREAKTHROUGH_AI_SECTION_LABELS } from '../../../types/proposal';
import { SectionAboutYou } from './sections/SectionAboutYou';
import { SectionRichText } from './sections/SectionRichText';
import { SectionCover } from './sections/breakthrough/SectionCover';
import { SectionCashPosition } from './sections/breakthrough/SectionCashPosition';
import { SectionLifestyleRetirement } from './sections/breakthrough/SectionLifestyleRetirement';
import { SectionPensionOptions } from './sections/breakthrough/SectionPensionOptions';
import { SectionTax } from './sections/breakthrough/SectionTax';
import { SectionFunds } from './sections/breakthrough/SectionFunds';

/** Section keys that may be removed once added — mirrors the addable set. */
export const BREAKTHROUGH_OPTIONAL_SECTION_KEYS: ReadonlySet<string> = new Set(
  BREAKTHROUGH_ADDABLE_SECTION_KEYS,
);

/**
 * Dispatches one Breakthrough Report section to its typed renderer — shared
 * by {@link BreakthroughReportBuilder}'s editable content pane and
 * {@link BreakthroughClientPreview}'s read-only document (BR-P5,
 * docs/specs/breakthrough-report-prd.md §5 / RQ-BR8's "Advisor view ↔ Client
 * view" toggle).
 *
 * Each section component owns its own `readOnly` branch: the parts that are
 * already read-only in the editable builder (facts cards, tables, charts,
 * server-owned figures) render unchanged either way; the editable
 * inputs/pickers/Save-Remove buttons render only when `readOnly` is false.
 * This keeps the Client view pixel-consistent with what the advisor already
 * sees for the non-editable parts of each section, without duplicating their
 * rendering logic in a second component tree.
 */
export function renderBreakthroughSection(
  section: ProposalSection,
  proposalId: string,
  profileId: string | undefined,
  proposal: Proposal,
  onSaved: (updated: ProposalSection) => void,
  onRemoved: (sectionKey: string) => void,
  onUpdateDetails: (partial: Partial<BreakthroughProposalDetails>) => Promise<void>,
  readOnly = false,
) {
  switch (section.sectionKey) {
    case 'BR_COVER':
      return (
        <SectionCover proposalId={proposalId} section={section} onSaved={onSaved} readOnly={readOnly} />
      );
    case 'BR_ABOUT_YOU':
      return <SectionAboutYou content={section.content as AboutYouSectionContent} />;
    case 'BR_CASH_POSITION':
      return (
        <SectionCashPosition
          proposalId={proposalId}
          section={section}
          onSaved={onSaved}
          readOnly={readOnly}
        />
      );
    case 'BR_LIFESTYLE_RETIREMENT':
      return (
        <SectionLifestyleRetirement
          proposalId={proposalId}
          profileId={profileId}
          proposal={proposal}
          section={section}
          onSaved={onSaved}
          onUpdateDetails={onUpdateDetails}
          readOnly={readOnly}
        />
      );
    case 'BR_PENSION_OPTIONS':
      return (
        <SectionPensionOptions
          proposalId={proposalId}
          profileId={profileId}
          proposal={proposal}
          section={section}
          onSaved={onSaved}
          onUpdateDetails={onUpdateDetails}
          readOnly={readOnly}
        />
      );
    case 'BR_TAX':
      return <SectionTax proposalId={proposalId} section={section} onSaved={onSaved} readOnly={readOnly} />;
    case 'BR_FUNDS':
      return (
        <SectionFunds
          proposalId={proposalId}
          proposal={proposal}
          section={section}
          onSaved={onSaved}
          onUpdateDetails={onUpdateDetails}
          readOnly={readOnly}
        />
      );
    default:
      return (
        <SectionRichText
          proposalId={proposalId}
          profileId={profileId}
          section={section}
          removable={BREAKTHROUGH_OPTIONAL_SECTION_KEYS.has(section.sectionKey)}
          onSaved={onSaved}
          onRemoved={onRemoved}
          aiSectionLabel={BREAKTHROUGH_AI_SECTION_LABELS[section.sectionKey]}
          aiUseCase="REPORT_BUILDER"
          readOnly={readOnly}
        />
      );
  }
}
